import { h, svg } from './dom'
import type { ComposerTarget, Draft, LocalNote, WidgetState } from './state'

/**
 * Renders saved annotations, the in-progress draft, and the annotation the
 * composer is currently about to save — all in *document* space.
 *
 * The widget's shadow host is `position: fixed`, which is what keeps it from
 * disturbing the host page's layout. That means the annotation layer inside it
 * has to be pushed back by the current scroll offset on every frame, otherwise
 * marks would float with the viewport instead of sticking to the content they
 * describe.
 */
export class Overlay {
  /** Scroll-tracking wrapper. Everything in document space lives under it. */
  private readonly root: HTMLElement
  /** Annotation marks. Rebuilt on every render. */
  private readonly marks: HTMLElement
  /** The composer. Mounted separately so a re-render cannot tear it down. */
  private readonly attached: HTMLElement
  private running = false
  private lastX = Number.NaN
  private lastY = Number.NaN

  constructor(shadow: ShadowRoot) {
    this.marks = h('div')
    this.attached = h('div')
    this.root = h('div', { class: 'doc-layer' }, this.marks, this.attached)
    shadow.appendChild(this.root)
  }

  /**
   * Keeps the annotation layer aligned with the document.
   *
   * This polls scroll position on an animation frame instead of listening for
   * `scroll`. That looks wasteful, but scroll events are not dependable here:
   * they do not fire for programmatic scrolls in some automation contexts, and
   * a host page that scrolls an inner container rather than the document emits
   * nothing on `window` at all. A misaligned pin is a correctness bug — it
   * points at the wrong element — so this reads the value that actually matters
   * every frame and writes only when it has changed.
   */
  attachScrollSync(): void {
    if (this.running) return
    this.running = true

    const tick = () => {
      const x = window.scrollX
      const y = window.scrollY
      if (x !== this.lastX || y !== this.lastY) {
        this.lastX = x
        this.lastY = y
        this.root.style.transform = `translate(${-x}px, ${-y}px)`
      }
      requestAnimationFrame(tick)
    }

    tick()
  }

  render(state: WidgetState): void {
    this.marks.textContent = ''

    for (const note of state.notes) {
      if (note.type === 'rect') this.marks.appendChild(rectEl(note))
      if (note.type === 'draw') this.marks.appendChild(sketchEl(note.points ?? []))
      this.marks.appendChild(badgeEl(note))
    }

    if (state.draft) this.marks.appendChild(draftEl(state.draft))

    // While the composer is open its annotation is neither a draft (the drag
    // has finished) nor a saved note (Save has not been pressed). Without this
    // the highlight or sketch you just drew vanishes at exactly the moment you
    // start describing it.
    if (state.composer) {
      for (const el of pendingEls(state.composer, state.notes.length + 1)) {
        this.marks.appendChild(el)
      }
    }
  }

  /**
   * The composer lives in document space too, so it tracks its annotation.
   * It is mounted outside `marks` because `render` rebuilds that subtree.
   */
  mount(node: HTMLElement): void {
    this.attached.appendChild(node)
  }
}

/**
 * The annotation as it will be saved, drawn while the composer is open. Uses
 * exactly the same elements as a saved note, so what you see while writing is
 * what you get afterwards.
 */
function pendingEls(target: ComposerTarget, n: number): HTMLElement[] {
  if (target.type === 'rect') {
    return [
      rectEl({ x: target.x, y: target.y, w: target.w, h: target.h }),
      badge(target.x, target.y, n),
    ]
  }

  if (target.type === 'draw') {
    const first = target.points[0]
    return [
      sketchEl(target.points),
      badge(first?.[0] ?? 0, first?.[1] ?? 0, n),
    ]
  }

  return [badge(target.x, target.y, n)]
}

function badgeAnchor(note: LocalNote): { x: number; y: number } {
  if (note.type === 'draw') {
    const first = note.points?.[0]
    return { x: first?.[0] ?? note.cx, y: first?.[1] ?? note.cy }
  }
  return { x: note.x ?? note.cx, y: note.y ?? note.cy }
}

function badge(x: number, y: number, n: number): HTMLElement {
  return h('div', { class: 'badge', style: `left:${x}px;top:${y}px;`, text: String(n) })
}

function badgeEl(note: LocalNote): HTMLElement {
  const { x, y } = badgeAnchor(note)
  return badge(x, y, note.n)
}

function rectEl(box: { x?: number; y?: number; w?: number; h?: number }): HTMLElement {
  return h('div', {
    class: 'rect',
    style: `left:${box.x}px;top:${box.y}px;width:${box.w}px;height:${box.h}px;`,
  })
}

function pointsAttr(points: [number, number][]): string {
  return points.map(([x, y]) => `${x},${y}`).join(' ')
}

function sketchEl(points: [number, number][], dashed = false): HTMLElement {
  const wrapper = h('div', { class: 'sketch', style: 'left:0;top:0;width:0;height:0;' })
  const canvas = svg('svg', { width: '1', height: '1', style: 'overflow:visible;' })
  canvas.appendChild(
    svg('polyline', {
      points: pointsAttr(points),
      fill: 'none',
      stroke: '#203de6',
      'stroke-width': '3',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      opacity: dashed ? '0.7' : '0.9',
      ...(dashed ? { 'stroke-dasharray': '6 5' } : {}),
    }),
  )
  wrapper.appendChild(canvas)
  return wrapper
}

function draftEl(draft: Draft): HTMLElement {
  if (draft.kind === 'rect') {
    const x = Math.min(draft.x, draft.x2)
    const y = Math.min(draft.y, draft.y2)
    return h('div', {
      class: 'rect-draft',
      style: `left:${x}px;top:${y}px;width:${Math.abs(draft.x2 - draft.x)}px;height:${Math.abs(draft.y2 - draft.y)}px;`,
    })
  }
  return sketchEl(draft.points, true)
}
